import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const TIERS = [
  {
    id: "essential",
    name: "Essential Care",
    badge: "Small Offices & Retail",
    price: "£349",
    period: "/mo from",
    featured: false,
    features: ["2 cleans per week", "Washroom & consumables care", "Monthly deep-clean sweep", "Email reporting"],
  },
  {
    id: "corporate",
    name: "Corporate Premier",
    badge: "Popular — Multi-Floor HQ",
    price: "£899",
    period: "/mo from",
    featured: true,
    features: ["Nightly weekday cleans", "Electrostatic disinfection cycle", "Real-time supervisor sign-offs", "Dedicated account manager", "Quarterly high-level clean"],
  },
  {
    id: "industrial",
    name: "Industrial & Enterprise",
    badge: "24/7 Operations & Venues",
    price: "Custom",
    period: " scoped quote",
    featured: false,
    features: ["24/7 rapid-response crews", "Heavy plant & degreasing", "Event turnaround teams", "Full compliance audit trail"],
  },
];

export const Pricing = () => (
  <section
    id="pricing"
    data-testid="pricing-section"
    className="relative py-24 sm:py-32"
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-14 flex items-end justify-between flex-wrap gap-6"
      >
        <div>
          <p className="eyebrow mb-4">Investment</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ice-50 leading-snug">
            Transparent packages, <span className="text-gold-500">tailored scope.</span>
          </h2>
        </div>
        <span
          data-testid="pricing-sample-badge"
          className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-500 border border-gold-500/40 rounded-full px-4 py-1.5"
        >
          Sample Only
        </span>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
        {TIERS.map((t, i) => (
          <motion.div
            key={t.id}
            data-testid={`pricing-card-${t.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className={`relative rounded-xl p-8 flex flex-col transition-all duration-300 hover:-translate-y-1.5 ${
              t.featured
                ? "bg-navy-800 border border-gold-500/50 shadow-[0_0_60px_-15px_rgba(245,158,11,0.35)]"
                : "bg-navy-800/40 border border-white/5 hover:border-white/15"
            }`}
          >
            {t.featured && (
              <span className="absolute -top-3 left-8 bg-gold-500 text-navy-900 font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 rounded-full">
                Most Popular
              </span>
            )}
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ice-200/50 mb-3">
              {t.badge}
            </p>
            <h3 className="font-heading font-bold text-2xl text-ice-50 mb-5">
              {t.name}
            </h3>
            <p className="mb-8">
              <span className="font-display font-black text-5xl text-gold-400">
                {t.price}
              </span>
              <span className="font-mono text-xs text-ice-200/50 ml-2">
                {t.period}
              </span>
            </p>
            <ul className="space-y-3.5 flex-1 mb-9">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-ice-200/75">
                  <Check className="w-4 h-4 text-gold-500 mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <button
              data-testid={`pricing-card-${t.id}-cta`}
              onClick={() => scrollToId("quote")}
              className={`group inline-flex items-center justify-center gap-2 font-heading font-bold text-sm uppercase tracking-widest px-6 py-3.5 rounded-full transition-all duration-300 ${
                t.featured
                  ? "bg-gold-500 text-navy-900 hover:bg-gold-400"
                  : "border border-ice-200/25 text-ice-50 hover:border-gold-500 hover:text-gold-400"
              }`}
            >
              Start With {t.name}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
